import { Delete, DoneWhiteSmall } from '@shared/assets/icons/components';
import { Button, Flex, Input, Select } from 'antd';
import type { FC } from 'react';
import styles from '../../integrations-item-fields.module.scss';
import type { AgentFieldSelectValue, DraftIntegrationField, SelectOption } from '../../lib/types';
import type { LocationType, WebhookMethodType } from '@shared/api';
import { Text } from '@shared/ui/antd';
import { useTranslation } from 'react-i18next';
import {
  getIntegrationFieldPresetByValue,
  INTEGRATION_FIELD_PRESET_VALUES,
} from '@modules/integrations/lib/integration-field-presets';

type IntegrationFieldRowProps = {
  field: DraftIntegrationField;
  requestMethod: WebhookMethodType;
  isBusy: boolean;
  isPersisted: boolean;
  isDisabled: boolean;
  isSaveDisabled: boolean;
  agentFieldOptions: SelectOption[];
  isSelectOpen: boolean;
  onSelectOpenChange: (open: boolean) => void;
  onChangeParameterName: (value: string) => void;
  onChangeAgentField: (value: AgentFieldSelectValue | null) => void;
  onChangeLocation: (value: LocationType) => void;
  onChangeCustomValue: (value: string) => void;
  onSave: () => void;
  onDelete: () => void;
};

export const IntegrationFieldRow: FC<IntegrationFieldRowProps> = ({
  field,
  requestMethod,
  isBusy,
  isPersisted,
  isDisabled,
  isSaveDisabled,
  agentFieldOptions,
  isSelectOpen,
  onSelectOpenChange,
  onChangeParameterName,
  onChangeAgentField,
  onChangeLocation,
  onChangeCustomValue,
  onSave,
  onDelete,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'integrations.fields' });
  const shouldShowSave = !isPersisted;
  const isBodyIgnored = requestMethod === 'GET' && field.location === 'body';
  const selectedAgentField =
    field.agentFieldId ?? getIntegrationFieldPresetByValue(field.value) ?? undefined;
  const agentFieldSelectOptions = [
    {
      label: t('presets.iframeLink'),
      value: INTEGRATION_FIELD_PRESET_VALUES.iframeLink,
    },
    ...agentFieldOptions,
  ];

  const locationOptions = [
    { label: t('location.query'), value: 'query' },
    { label: t('location.body'), value: 'body' },
    { label: t('location.header'), value: 'header' },
  ];

  return (
    <div className={styles.row}>
      <div className={styles.rowContent}>
        <div className={styles.fieldColumn}>
          <Text variant="sm" fontWeight={400} className={styles.fieldLabel}>
            {t('systemFieldLabel')}
          </Text>
          <Input
            value={field.parameterName}
            className={styles.input}
            placeholder={t('systemFieldPlaceholder')}
            disabled={isDisabled}
            size="large"
            onChange={(e) => onChangeParameterName(e.target.value)}
          />
        </div>

        <div className={styles.fieldColumn}>
          <Text variant="sm" fontWeight={400} className={styles.fieldLabel}>
            {t('agentFieldLabel')}
          </Text>
          <Select
            value={selectedAgentField}
            className={styles.input}
            placeholder={t('agentFieldPlaceholder')}
            disabled={isDisabled}
            size="large"
            options={agentFieldSelectOptions}
            allowClear
            open={isSelectOpen}
            onDropdownVisibleChange={onSelectOpenChange}
            onChange={(value) => onChangeAgentField(value ?? null)}
          />
        </div>

        <div className={styles.fieldColumn}>
          <Text variant="sm" fontWeight={400} className={styles.fieldLabel}>
            {t('defaultValueLabel')}
          </Text>
          <Input
            value={field.value ?? ''}
            className={styles.input}
            placeholder={t('defaultValuePlaceholder')}
            disabled={isDisabled}
            size="large"
            showCount
            maxLength={256}
            onChange={(e) => onChangeCustomValue(e.target.value)}
          />
        </div>

        <div className={styles.fieldColumn}>
          <Text
            variant="sm"
            fontWeight={400}
            className={`${styles.fieldLabel} ${isBodyIgnored ? styles.fieldLabelError : ''}`}
          >
            {t('dataTypeLabel')}
          </Text>
          <Select
            value={field.location}
            className={styles.locationSelect}
            status={isBodyIgnored ? 'error' : undefined}
            disabled={isDisabled}
            size="large"
            options={locationOptions}
            onChange={(value) => onChangeLocation(value as LocationType)}
            aria-label={t('dataTypeLabel')}
          />
        </div>

        {isBodyIgnored && (
          <Text variant="xs" className={styles.bodyWarning} role="status">
            {t('bodyIgnoredForGet')}
          </Text>
        )}
      </div>

      <Flex gap={8} className={styles.actions} vertical>
        <Button
          type="text"
          className={styles.deleteButton}
          icon={<Delete color={isBusy ? '#A3A3A3' : '#ED131C'} width={20} height={20} />}
          disabled={isBusy}
          onClick={onDelete}
          aria-label={t('aria.delete')}
        />

        {shouldShowSave && (
          <Button
            type="primary"
            icon={<DoneWhiteSmall />}
            loading={isBusy}
            disabled={isSaveDisabled}
            className={styles.submitButton}
            onClick={onSave}
            aria-label={t('aria.save')}
          />
        )}
      </Flex>
    </div>
  );
};
